import { useState } from 'react';
import { useNavigate } from 'react-router';
import { InvestorsFilters } from '@/components/investors/InvestorsFilters';
import { InvestorsTable } from '@/components/investors/InvestorsTable';
import { useInvestorsPageData } from '@/hooks/react-query/useInvestors';
import { useInvestorFilters } from '@/hooks/useInvestorFilters';
import { InvestorImportWizard } from '@/components/investor-import/InvestorImportWizard';
import { InvestorsPageHeader } from '@/components/investors/InvestorsPageHeader';
import { InvestorStatisticsCards } from '@/components/investors/InvestorStatisticsCards';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { InvestorsEmptyState } from '@/components/investors/InvestorsEmptyState';

export default function InvestorsPage() {
  const navigate = useNavigate();

  // Use custom hook for filter and pagination state management
  const {
    filters,
    searchInput,
    showPendingChangesOnly,
    page,
    pageSize,
    handleFilterChange,
    clearFilters,
    handlePageChange,
    handlePageSizeChange,
  } = useInvestorFilters();

  // Use React Query to fetch investors data with pagination
  const { investors, pagination, statistics, refetch } = useInvestorsPageData(
    filters,
    { page, page_size: pageSize }
  );

  // Import wizard state
  const [showImportWizard, setShowImportWizard] = useState(false);

  // Keyboard shortcuts
  const investorShortcuts = [
    {
      key: 'n',
      description: 'Create new investor',
      action: () => navigate('/app/investors/new'),
      category: 'Investor Management',
    },
    {
      key: 'i',
      description: 'Import investors',
      action: () => setShowImportWizard(true),
      category: 'Investor Management',
    },
    {
      key: '/',
      description: 'Focus search',
      action: () => {
        const searchInput = document.getElementById(
          'investor-search'
        ) as HTMLInputElement;
        searchInput?.focus();
      },
      category: 'Navigation',
    },
    {
      key: '↑/↓',
      description: 'Navigate table rows',
      action: () => {}, // Handled by handleKeyDown
      category: 'Navigation',
    },
    {
      key: 'Enter',
      description: 'Select highlighted investor',
      action: () => {}, // Handled by handleKeyDown
      category: 'Navigation',
    },
    {
      key: 'Escape',
      description: 'Clear row selection',
      action: () => {}, // Handled by handleKeyDown
      category: 'Navigation',
    },
  ];

  useKeyboardShortcuts('investors-page', investorShortcuts);

  return (
    <>
      <div className="mx-auto p-6 space-y-6">
        {/* Page Header */}
        <InvestorsPageHeader
          hasInvestors={investors.length > 0}
          filters={filters}
          fundFilterActive={!!filters.fund}
          onClearFundFilter={() => handleFilterChange('fund', undefined)}
          onImport={() => setShowImportWizard(true)}
          onNewInvestor={() => navigate('/app/investors/new')}
        />

        {/* Summary Statistics */}
        <InvestorStatisticsCards
          statistics={statistics}
          isFundView={!!filters.fund}
        />

        {/* Filters and Search */}
        <InvestorsFilters
          filters={filters}
          searchValue={searchInput}
          onFilterChange={handleFilterChange}
          onClearFilters={clearFilters}
          showPendingChangesOnly={showPendingChangesOnly}
        />

        {/* Investors Table */}
        {investors.length > 0 ? (
          <InvestorsTable
            investors={investors}
            fundId={filters.fund}
            pagination={pagination}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        ) : (
          /* Empty State */
          <InvestorsEmptyState
            hasFilters={true}
            hasInvestors={investors.length > 0}
            onNewInvestor={() => navigate('/app/investors/new')}
          />
        )}
      </div>
      {/* Import Wizard */}
      {showImportWizard && (
        <InvestorImportWizard
          onClose={() => {
            setShowImportWizard(false);
            // Refresh the investors data after import
            refetch();
          }}
        />
      )}
    </>
  );
}
